import React from "react";
import {
    Trees,
    Layers,
    Ruler,
    Boxes,
    Truck,
    BadgeDollarSign,
} from "lucide-react";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { Button } from "../components/zen/Button";
import { PageHero } from "../components/zen/PageHero";
import { CapabilityCard } from "../components/zen/CapabilityCard";
import { ClosingCTA } from "../components/zen/ClosingCTA";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormSuccess,
    FormCard,
    useLeadSubmit,
} from "../components/zen/FormField";
import { FormSubmit } from "../components/zen/FormSubmit";

const CAPABILITIES = [
    { icon: Trees, title: "Rough Pine", description: "Bulk pine supply at competitive wholesale pricing." },
    { icon: Layers, title: "Hardwood", description: "Premium hardwood lots for furniture, flooring, and millwork." },
    { icon: Ruler, title: "Custom Dimensions", description: "Cut-to-spec timber for commercial and industrial projects." },
    { icon: Boxes, title: "Logs & Raw Timber", description: "Direct-from-source raw timber for processors and mills." },
    { icon: Truck, title: "Logistics & Freight", description: "Coordinated freight across the region with reliable scheduling." },
    { icon: BadgeDollarSign, title: "Volume Pricing", description: "Negotiated pricing structures for sustained-volume buyers." },
];

const STATS = [
    { label: "Regional Coverage", value: "Multi-State" },
    { label: "Volume Capable", value: "50+ Loads / Mo" },
    { label: "Custom Dimensions", value: "On-Spec" },
    { label: "Direct Supply", value: "20+ Mills" },
];

const FORM_FIELDS = [
    { name: "company_name", required: true, type: "text" },
    { name: "contact_name", required: true, type: "text" },
    { name: "email", required: true, type: "email" },
    { name: "phone", required: true, type: "text" },
    { name: "material_type", required: true, type: "select" },
    { name: "estimated_volume", required: true, type: "text" },
    { name: "delivery_location", required: true, type: "text" },
    { name: "project_notes", required: false, type: "text" },
];

export default function BuildingMaterials() {
    const form = useLeadSubmit({ endpoint: "/leads/materials", fields: FORM_FIELDS });

    return (
        <PageShell>
            <div data-testid="page-building-materials">
                <PageHero
                    eyebrow="Building Materials"
                    title="Wholesale timber and industrial supply."
                    subtitle="Rough pine, hardwood, and custom-dimension timber for commercial buyers, contractors, and industrial partners. Direct supply, transparent pricing, reliable logistics."
                    data-testid="bm-hero"
                    logoSrc="/logos/citywood.png"
                    logoAlt="CityWood — wholesale timber supply"
                    logoMaxHeightClass="max-h-24 sm:max-h-28"
                    logoTagline="A division of ZenMe Group LLC"
                >
                    <Button href="#quote" variant="primary" size="lg" data-testid="bm-cta-quote">Request Pricing</Button>
                    <Button to="/contact" variant="ghost" size="lg" data-testid="bm-cta-contact">Talk to Sales</Button>
                </PageHero>

                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Supply Capabilities</Eyebrow>
                            <Heading as="h2" className="mt-4">Built for sustained-volume buyers.</Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {CAPABILITIES.map((c, i) => (
                                <CapabilityCard key={c.title} {...c} data-testid={`bm-capability-${i}`} />
                            ))}
                        </div>
                    </Container>
                </Section>

                <section className="relative border-y border-border-subtle bg-elevated" data-testid="bm-stats">
                    <Container>
                        <div className="py-14 lg:py-16">
                            <div className="grid grid-cols-2 gap-y-10 gap-x-6 lg:grid-cols-4">
                                {STATS.map((s) => (
                                    <div key={s.label} className="flex flex-col items-start">
                                        <span className="font-display font-semibold leading-none text-[clamp(22px,2.4vw,30px)] tracking-tight-2" style={{ color: "#E8FFD8" }}>{s.value}</span>
                                        <span className="mt-3 text-[12px] uppercase tracking-widest-2 text-text-muted">{s.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Container>
                </section>

                <Section id="quote" className="scroll-mt-24 border-b border-border-subtle">
                    <Container>
                        <div className="mx-auto max-w-2xl text-center">
                            <Eyebrow>Request Pricing</Eyebrow>
                            <Heading as="h2" className="mt-4">Tell us what you need.</Heading>
                            <p className="mt-5 text-[16px] leading-[1.65] text-text-secondary">Send your specs and we'll respond with a tailored quote within one business day.</p>
                        </div>
                        <div className="mx-auto mt-12 max-w-2xl">
                            <FormCard data-testid="bm-form-card">
                                {form.success ? (
                                    <FormSuccess title="Received." message="Thanks for your inquiry — our supply team will respond with pricing within one business day." data-testid="bm-form-success" />
                                ) : (
                                    <form onSubmit={form.onSubmit} noValidate className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="bm-form">
                                        <FormField label="Company Name" name="company_name" required value={form.values.company_name} onChange={form.onChange} error={form.errors.company_name} data-testid="bm-input-company" />
                                        <FormField label="Contact Name" name="contact_name" required value={form.values.contact_name} onChange={form.onChange} error={form.errors.contact_name} data-testid="bm-input-contact" />
                                        <FormField label="Email" name="email" type="email" required value={form.values.email} onChange={form.onChange} error={form.errors.email} data-testid="bm-input-email" />
                                        <FormField label="Phone" name="phone" type="tel" required value={form.values.phone} onChange={form.onChange} error={form.errors.phone} data-testid="bm-input-phone" />
                                        <FormSelect label="Material Type" name="material_type" required value={form.values.material_type} onChange={form.onChange} error={form.errors.material_type} options={["Rough Pine", "Hardwood", "Custom Dimensions", "Logs/Raw Timber", "Other"]} data-testid="bm-input-material" />
                                        <FormField label="Estimated Volume" name="estimated_volume" required placeholder="e.g., 5 trailer loads / month" value={form.values.estimated_volume} onChange={form.onChange} error={form.errors.estimated_volume} data-testid="bm-input-volume" />
                                        <div className="sm:col-span-2">
                                            <FormField label="Delivery Location" name="delivery_location" required placeholder="City, State" value={form.values.delivery_location} onChange={form.onChange} error={form.errors.delivery_location} data-testid="bm-input-location" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <FormTextarea label="Project Notes" name="project_notes" placeholder="Specs, timeline, or other context (optional)" value={form.values.project_notes} onChange={form.onChange} data-testid="bm-input-notes" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <FormSubmit label="Request Pricing" submitting={form.submitting} data-testid="bm-submit-btn" />
                                        </div>
                                    </form>
                                )}
                            </FormCard>
                        </div>
                    </Container>
                </Section>

                <ClosingCTA eyebrow="Trusted Supply" title="Direct supply at industrial scale." primary={{ label: "Contact Us", to: "/contact" }} secondary={{ label: "Explore Divisions", to: "/divisions" }} />
            </div>
        </PageShell>
    );
}
